import joi from 'joi';
import z from 'zod';
import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  Page,
  PaginationQueryParams,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { $bool } from '../zod/utils/boolean';
import { AddCustomKeyBody, CustomKeyAttributes } from './types/custom-key';
import { ApplyImpactBody } from './types/impact';
import {
  ApplyImpactResponse,
  GetProfileIncludeOptions,
  ProfileAttributes,
  ProfileUpsert,
  UpsertProfileResponse,
} from './types/profile';
import {
  AssignPromoCodePayload,
  ProfileRewardAttributes,
  TransactionAttributes,
  TransactionI18nAttributes,
} from './types/transaction';
import { CleanupInactiveProfilesResponse } from './types/cleanup-inactive-profiles';
import { ClickPayload } from './types/click';
import {
  CreateInteractionBody,
  InteractionAttributes,
  ProfileInteractionAttributes,
  ProfileInteractionPayload,
  UpdateInteractionBody,
} from './types/interaction';
import {
  EarnEvent,
  EarnEventAttributes,
  EcommerceEventPayload,
  EventProfile,
} from '../ingestor-types';
import { ExportProfilesQuery } from './schemas/export-profiles-query-schema';
import { IdentityAttributes } from './types/identifier';
import { LevelAttributes } from '../gamification-api-client/types/level';
import { LevelsConfigAttributes } from '../gamification-api-client/types/level-config';
import { MembershipAttributes } from './types/membership';
import { RequiredProps } from '../utils';
import { TriggerExpireTransactionsResponse } from './types/trigger-expire-transactions';
import { UpsertProfileQuery } from './schemas/profile-upsert-schema';
import { UpsertProfilesResponse } from './types/upsert-profiles';
import { Visit } from './types/visit';

export interface GetTransactionsParameters {
  ruleId?: string;
  programId?: string;
  currencyIds?: string[];
  limit?: number;
  skip?: number;
  orderBy?: `${'-' | '+' | ''}${keyof TransactionAttributes}`;
  startDate?: Date;
  endDate?: Date;
}

export interface GetProfileInteractionsParameters {
  programId?: string;
  interactionIds?: string[];
  limit?: number;
  skip?: number;
}

export interface GetProfileInteractionsPayload {
  data: ProfileInteractionAttributes[];
  isLast: boolean;
  pageCount: number;
}

export interface GetProfileRewardsParameters {
  programId?: string;
  limit?: number;
  skip?: number;
  orderBy?: `${'-' | '+' | ''}${keyof ProfileRewardAttributes}`;
}

export interface GetProfileQueryParams {
  include?: GetProfileIncludeOptions[];
  programId?: string;
  withDeleted?: boolean;
}

export interface ListInteractionsQueryParams {
  withDeleted?: boolean;
}

export interface SearchProfilesQueryParams {
  accountId?: string;
  programId?: string;
  withCurrencies?: boolean;
  withRewards?: boolean;
  pageSize?: number;
  pageNumber?: number;
  ids?: string[];
  search?: string;
  isTestUser?: boolean;
}

export const $DeleteProfileQueryParamsSchema = z.object({
  forceDelete: $bool.optional().default(false),
});

export type DeleteProfileQueryParams = z.infer<typeof $DeleteProfileQueryParamsSchema>;

export interface SearchCustomKeysQueryParams {
  pageSize?: number;
}

export const $getAuthTokenQuery = z.object({
  profileId: z.string().optional(),
  programId: z.string(),
});
export type GetAuthTokenQuery = z.infer<typeof $getAuthTokenQuery>;

export const transactionI18nAttributesSchema = joi.object<TransactionI18nAttributes>().pattern(
  /[a-z]+/i,
  joi.object({
    description: joi
      .string()
      .custom((value: string) => (value.startsWith('<p>') ? value : `<p>${value}</p>`)),
  }),
);

export interface CreateTransactionPayload {
  programId: string;
  profileId: string;
  currencyId: string;
  i18nAttributes: TransactionI18nAttributes;
  amount: number;
}

export interface GetTransactionsPayload {
  data: TransactionAttributes[];
  isLast: boolean;
  pageCount: number;
}

export interface GetProfileRewardsPayload {
  data: ProfileRewardAttributes[];
  isLast: boolean;
  pageCount: number;
}

export interface UpdateMemberLevelData {
  levelsConfig?: LevelsConfigAttributes;
  levels: LevelAttributes[];
  forcedLevel?: LevelAttributes | null;
  forcedProgression?: number;
}

export interface DeleteMemberResponse {
  membershipsLeft: number;
  accountId: string;
  profileDeleted: boolean;
}

export interface RecordEcommerceReferralResponse {
  message: string;
  isValid: boolean;
  referrer: ProfileAttributes | undefined;
}

export const $ProfileByIdentityBody = z.object({
  kind: z.string(),
  value: z.string().toLowerCase(),
});

export type ProfileByIdentityBody = z.infer<typeof $ProfileByIdentityBody>;

export interface GetProfileByIdentityQuery {
  programId?: string;
  include?: GetProfileIncludeOptions[];
  withDeleted?: boolean;
}

export type GetProfileByIdentityResponse = RequiredProps<
  ProfileAttributes,
  'identities' | 'memberships'
>;

export const $CountTransactionsOverPeriodQuery = z.object({
  ruleId: z.string(),
  bounds: z
    .object({
      startDate: z.coerce.date(),
      endDate: z.coerce.date(),
    })
    .optional(),
});

export type CountTransactionsOverPeriodQuery = z.infer<typeof $CountTransactionsOverPeriodQuery>;

export class ProfileApiClient extends ApiClient {
  static serviceName = 'profile-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/profile', http, context, options);
  }

  async lookupProfileByIdentity(
    accountId: string,
    { kind, value }: EventProfile,
    options?: RouteOptions<GetProfileByIdentityQuery>,
  ): Promise<GetProfileByIdentityResponse> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/profiles/lookup`,
      { kind, value },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async recordEcommerceReferral(
    refereeId: string,
    referralCode: string,
    ecommercePayload: EarnEventAttributes<EarnEvent<EcommerceEventPayload>>,
    options?: RouteOptions,
  ): Promise<RecordEcommerceReferralResponse> {
    const { data } = await this.http.put(
      `/profiles/${refereeId}/ecommerce-referral/${referralCode}`,
      ecommercePayload,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getProfile(
    profileId: string,
    options?: RouteOptions<GetProfileQueryParams>,
  ): Promise<ProfileAttributes> {
    const { data } = await this.http.get(`/profiles/${profileId}`, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        include: options?.params?.include,
        programId: options?.params?.programId,
        withDeleted: options?.params?.withDeleted,
      },
    });
    return data;
  }

  async getProfileIdentities(
    profileId: string,
    options?: RouteOptions,
  ): Promise<IdentityAttributes[]> {
    const { data } = await this.http.get(
      `/profiles/${profileId}/identities`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  /**
   * @deprecated
   * @param accountId
   * @param options
   */
  async getProfiles(accountId: string, options?: RouteOptions): Promise<ProfileAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/profiles`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async streamProfiles(accountId: string, options?: RouteOptions): Promise<any> {
    const { data } = await this.http.get<ReadableStream>(`/accounts/${accountId}/stream-profiles`, {
      ...defaultAxiosConfig(options),
      responseType: 'stream',
    });
    return data;
  }

  /**
   * Streams profiles for a program as JSONL. Resumable via startNumericalId.
   * Requires at least one of createdAtFrom, lastVisitFrom, updatedAtFrom.
   */
  async exportProfiles(
    programId: string,
    params: ExportProfilesQuery,
    options?: RouteOptions,
  ): Promise<any> {
    const { data } = await this.http.get(`/programs/${programId}/profiles`, {
      ...defaultAxiosConfig(options),
      params,
      responseType: 'stream',
    });
    return data;
  }

  async searchProfiles(
    options?: RouteOptions<SearchProfilesQueryParams>,
  ): Promise<Page<ProfileAttributes>> {
    const { data } = await this.http.get(`/search`, defaultAxiosConfig(options));
    return data;
  }

  async upsertProfile(
    accountId: string,
    profile: ProfileUpsert,
    options?: RouteOptions<UpsertProfileQuery>,
  ): Promise<UpsertProfileResponse> {
    const response = await this.http.put<UpsertProfileResponse>(
      `/accounts/${accountId}/profiles`,
      profile,
      defaultAxiosConfig(options),
    );

    profile.memberships?.forEach(({ programId }) => {
      this.invalidateGetProfile(response.data.profile.id, programId, response.config.baseURL);
    });
    return response.data;
  }

  async upsertProfiles(
    accountId: string,
    profiles: ProfileUpsert[],
    options?: RouteOptions<{ lookupByIdentity?: string; programId?: string }>,
  ): Promise<UpsertProfilesResponse> {
    const response = await this.http.put(
      `/accounts/${accountId}/profiles/bulk`,
      profiles,
      defaultAxiosConfig(options),
    );
    profiles.forEach(profile => {
      profile.memberships?.forEach(({ programId }) => {
        if (profile.id) {
          this.invalidateGetProfile(profile.id, programId, response.config.baseURL);
        }
      });
    });
    return response.data;
  }

  async applyImpact(body: ApplyImpactBody, options?: RouteOptions): Promise<ApplyImpactResponse> {
    const response = await this.http.patch<ApplyImpactResponse>(
      '/profiles/impact',
      body,
      defaultAxiosConfig(options),
    );
    const resultImpact = response.data;
    resultImpact.transactions.forEach(transaction => {
      this.invalidateGetProfile(
        body.impact.profileId,
        transaction.programId,
        response.config.baseURL,
      );
    });
    return response.data;
  }

  async assignReward(
    profileId: string,
    reward: AssignPromoCodePayload,
    options?: RouteOptions,
  ): Promise<ProfileRewardAttributes> {
    const response = await this.http.patch(
      `/profiles/${profileId}/assign-reward`,
      reward,
      defaultAxiosConfig(options),
    );
    const profileReward = response.data;
    this.invalidateGetProfile(profileId, profileReward.programId, response.config.baseURL);
    return profileReward;
  }

  getTransactions(
    profileId: string,
    params: GetTransactionsParameters,
    options?: RouteOptions,
  ): Promise<GetTransactionsPayload> {
    return this.http
      .get<GetTransactionsPayload>(`/profiles/${profileId}/transactions`, {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          ...params,
        },
      })
      .then(({ data }) => data);
  }

  async listInteractions(
    accountId: string,
    options?: RouteOptions<ListInteractionsQueryParams>,
  ): Promise<InteractionAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/interactions`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getInteraction(
    interactionId: string,
    options?: RouteOptions,
  ): Promise<InteractionAttributes> {
    const { data } = await this.http.get(
      `/interactions/${interactionId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async createInteraction(
    accountId: string,
    body: CreateInteractionBody,
    options?: RouteOptions,
  ): Promise<InteractionAttributes> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/interactions`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateInteraction(
    interactionId: string,
    body: UpdateInteractionBody,
    options?: RouteOptions,
  ): Promise<InteractionAttributes> {
    const { data } = await this.http.put(
      `/interactions/${interactionId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteInteraction(interactionId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/interactions/${interactionId}`, defaultAxiosConfig(options));
  }

  getProfileInteractions(
    profileId: string,
    params: GetProfileInteractionsParameters,
    options?: RouteOptions,
  ): Promise<GetProfileInteractionsPayload> {
    return this.http
      .get<GetProfileInteractionsPayload>(`/profiles/${profileId}/interactions`, {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          ...params,
        },
      })
      .then(({ data }) => data);
  }

  getProfileInteraction(
    profileId: string,
    profileInteractionId: string,
    options?: RouteOptions,
  ): Promise<ProfileInteractionPayload> {
    return this.http
      .get<ProfileInteractionPayload>(
        `/profiles/${profileId}/interactions/${profileInteractionId}`,
        defaultAxiosConfig(options),
      )
      .then(({ data }) => data);
  }

  countTransactionsOverPeriod(
    profileId: string,
    params: CountTransactionsOverPeriodQuery,
    options?: RouteOptions,
  ): Promise<number> {
    return this.http
      .get<number>(`/profiles/${profileId}/transactions-count-over-period`, {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          ...params,
        },
      })
      .then(({ data }) => data);
  }

  getProfileRewards(
    profileId: string,
    params: GetProfileRewardsParameters,
    options?: RouteOptions,
  ): Promise<GetProfileRewardsPayload> {
    return this.http
      .get<GetProfileRewardsPayload>(`/profiles/${profileId}/rewards`, {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          ...params,
        },
      })
      .then(({ data }) => data);
  }

  getProfileReward(
    profileRewardId: string,
    options?: RouteOptions,
  ): Promise<ProfileRewardAttributes> {
    return this.http
      .get<ProfileRewardAttributes>(`/rewards/${profileRewardId}`, defaultAxiosConfig(options))
      .then(({ data }) => data);
  }

  //useful ? not used
  getTransactionsByIds(ids: string[], options?: RouteOptions): Promise<TransactionAttributes[]> {
    return this.http
      .get<TransactionAttributes[]>('/transactions/by-ids', {
        ...defaultAxiosConfig(options),
        params: { ...options?.params, ids },
      })
      .then(({ data }) => data);
  }

  async recordVisit(visit: Visit, options?: RouteOptions): Promise<void> {
    const response = await this.http.post('/visits', visit, defaultAxiosConfig(options));
    this.invalidateGetProfile(visit.profileId, visit.programId, response.config.baseURL);
  }

  async recordClick(clickPayload: ClickPayload, options?: RouteOptions): Promise<void> {
    const response = await this.http.post('/clicks', clickPayload, defaultAxiosConfig(options));
    this.invalidateGetProfile(
      clickPayload.profileId,
      clickPayload.programId,
      response.config.baseURL,
    );
  }

  async blockMember(programId: string, profileId: string, options?: RouteOptions): Promise<void> {
    const response = await this.http.post(
      `/programs/${programId}/profiles/${profileId}/block`,
      undefined,
      defaultAxiosConfig(options),
    );
    this.invalidateGetProfile(profileId, programId, response.config.baseURL);
  }

  async unblockMember(programId: string, profileId: string, options?: RouteOptions): Promise<void> {
    const response = await this.http.post(
      `/programs/${programId}/profiles/${profileId}/unblock`,
      undefined,
      defaultAxiosConfig(options),
    );
    this.invalidateGetProfile(profileId, programId, response.config.baseURL);
  }

  async deleteProfile(
    profileId: string,
    options?: RouteOptions<DeleteProfileQueryParams>,
  ): Promise<void> {
    const { data, config } = await this.http.delete(`/profiles/${profileId}`, {
      ...defaultAxiosConfig(options),
      params: options?.params,
    });
    this.invalidateGetProfile(profileId, undefined, config.baseURL);
  }

  async deleteIdentity(profileId: string, kind: string, options?: RouteOptions): Promise<void> {
    const response = await this.http.delete(
      `/identities/${profileId}/${kind}`,
      defaultAxiosConfig(options),
    );
    this.invalidateGetProfile(profileId, undefined, response.config.baseURL);
  }

  async getMembership(
    profileId: string,
    programId: string,
    options?: RouteOptions,
  ): Promise<MembershipAttributes> {
    const { data } = await this.http.get(
      `/profiles/${profileId}/memberships/${programId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getCustomKeys(
    options?: RouteOptions<PaginationQueryParams>,
  ): Promise<Page<CustomKeyAttributes>> {
    const { data } = await this.http.get(`/custom-keys`, defaultAxiosConfig(options));
    return data;
  }

  async getAllCustomKeys(options?: RouteOptions): Promise<CustomKeyAttributes[]> {
    let pageNumber = 0;
    let customKeyPage: Page<CustomKeyAttributes>;
    const accumulatedCustomKeys: CustomKeyAttributes[] = [];
    do {
      customKeyPage = await this.getCustomKeys({
        ...options,
        params: {
          ...options?.params,
          pageSize: 100,
          pageNumber,
        },
      });
      accumulatedCustomKeys.push(...customKeyPage.data);
      pageNumber++;
    } while (!customKeyPage.isLast);
    return accumulatedCustomKeys;
  }

  async getAccountCustomKeys(
    accountId: string,
    options?: RouteOptions,
  ): Promise<CustomKeyAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/custom-keys`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getCustomKey(customKeyId: string, options?: RouteOptions): Promise<CustomKeyAttributes> {
    const { data } = await this.http.get(
      `/custom-keys/${customKeyId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addCustomKey(
    accountId: string,
    customKey: AddCustomKeyBody,
    options?: RouteOptions,
  ): Promise<CustomKeyAttributes> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/custom-keys`,
      customKey,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteCustomKey(accountId: string, customKeyId: string, options?: RouteOptions) {
    const { data } = await this.http.delete(
      `/accounts/${accountId}/custom-keys/${customKeyId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateCustomKey(
    customKeyId: string,
    customKey: AddCustomKeyBody,
    options?: RouteOptions,
  ): Promise<CustomKeyAttributes> {
    const { data } = await this.http.put(
      `/custom-keys/${customKeyId}`,
      customKey,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async replaceLevel(
    programId: string,
    oldLevelId: string,
    newLevelId: string | null,
    options?: RouteOptions,
  ): Promise<{ updated: number }> {
    const { data } = await this.http.put(
      `/programs/${programId}/levels/${oldLevelId}/${newLevelId}`, // null to 'null' is intended
      undefined,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateMemberLevel(
    programId: string,
    profileId: string,
    data: UpdateMemberLevelData,
    options?: RouteOptions,
  ): Promise<void> {
    await this.http.put(
      `/programs/${programId}/profiles/${profileId}/level`,
      data,
      defaultAxiosConfig(options),
    );
  }

  async searchCustomKeysByAccount(
    accountId: string,
    search: string,
    options?: RouteOptions<SearchCustomKeysQueryParams>,
  ): Promise<CustomKeyAttributes[]> {
    const { data } = await this.http.get(`/accounts/${accountId}/custom-keys/search`, {
      ...defaultAxiosConfig(options),
      params: {
        search,
      },
    });
    return data;
  }

  async deleteProgramProfilesData(
    programId: string,
    options?: RouteOptions<DeleteProfileQueryParams>,
  ): Promise<void> {
    await this.http.delete(`/programs/${programId}/reset`, {
      ...defaultAxiosConfig(options),
      params: options?.params,
    });
  }

  async deleteMember(
    programId: string,
    profileId: string,
    options?: RouteOptions,
  ): Promise<DeleteMemberResponse> {
    const { data } = await this.http.delete(
      `/programs/${programId}/profiles/${profileId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  invalidateGetProfile(profileId: string, programId?: string, baseURL?: string) {
    const url = `/profiles/${profileId}`;
    this.invalidate({
      baseURL,
      url,
      params: {
        withDeleted: undefined,
      },
    });
    if (programId) {
      this.invalidate({
        baseURL,
        url,
        params: {
          include: [
            GetProfileIncludeOptions.currencies,
            GetProfileIncludeOptions.rewards,
            GetProfileIncludeOptions.memberships,
            GetProfileIncludeOptions.identities,
          ],
          programId,
          withDeleted: undefined,
        },
      });
      this.invalidate({
        baseURL,
        url,
        params: {
          include: [GetProfileIncludeOptions.currencies, GetProfileIncludeOptions.memberships],
          programId,
          withDeleted: undefined,
        },
      });
    }
  }

  // not used in the code but can be useful to force reevaluation of levels for a program after settings changed
  async evaluateMembershipsLevels(
    programId: string,
    levelConfig: LevelsConfigAttributes,
    levels: LevelAttributes[],
    options?: RouteOptions,
  ): Promise<number> {
    const { data } = await this.http.patch(
      `/programs/${programId}/memberships/evaluate-levels`,
      { levelConfig, levels },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async processExpiredLevels(
    programId: string,
    levelConfig: LevelsConfigAttributes,
    levels: LevelAttributes[],
    options?: RouteOptions,
  ): Promise<number> {
    const { data } = await this.http.patch(
      `/programs/${programId}/memberships/process-expired-levels`,
      { levelConfig, levels },
      defaultAxiosConfig(options),
    );
    return data;
  }

  /**
   * @deprecated
   */
  async tempAddChannelInfo(accountId: string, options?: RouteOptions): Promise<void> {
    await this.http.post(`/tmp/add-channel-info`, { accountId }, defaultAxiosConfig(options));
  }

  async manuallyUpdateCurrenciesExpiration(
    currencyId: string,
    newExpirationDate: Date | null,
    options?: RouteOptions,
  ): Promise<void> {
    await this.http.post(
      `/currencies/${currencyId}/update-expiration`,
      { newExpirationDate },
      defaultAxiosConfig(options),
    );
  }

  async updateLevelsExpiration(
    programId: string,
    newExpirationDate: Date | null,
    options?: RouteOptions,
  ): Promise<number> {
    const { data } = await this.http.put(
      `/programs/${programId}/memberships`,
      { level: { expireAt: newExpirationDate } },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async exportWonRewards(
    accountId: string,
    rewardId: string,
    options?: RouteOptions,
  ): Promise<{ data: Blob; filename: string }> {
    const response = await this.http.get(`/accounts/${accountId}/rewards/${rewardId}/export`, {
      ...defaultAxiosConfig(options),
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="?([^";\n]+)"?/)?.[1] ?? 'export.csv';
    return { data: response.data, filename };
  }

  async deleteProfileReward(profileRewardId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/rewards/${profileRewardId}`, defaultAxiosConfig(options));
  }

  async triggerCleanupInactiveProfiles(
    programId: string,
    options?: RouteOptions,
  ): Promise<CleanupInactiveProfilesResponse> {
    const { data } = await this.http.post<CleanupInactiveProfilesResponse>(
      `/programs/${programId}/cleanup-inactive-profiles`,
      undefined,
      {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
        },
      },
    );
    return data;
  }

  async triggerExpireTransactions(
    options?: RouteOptions,
  ): Promise<TriggerExpireTransactionsResponse> {
    const { data } = await this.http.post<TriggerExpireTransactionsResponse>(
      `/tasks/trigger-expire-transactions`,
      {},
      defaultAxiosConfig(options),
    );
    return data;
  }
}
